import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Table, Button, Spin, Empty, Typography, Space, message } from 'antd';
import { PrinterOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { API_BASE_URL } from '../../utils/config';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
dayjs.locale('tr');
const { Title, Text } = Typography;

const minioUrl = 'https://dosya.atmturkiye.com/atmpoint/ibbsunum-logo/';
const firmaLogos = {
  1: `${minioUrl}atmpoint.png`,       
  2: `${minioUrl}masef.png`,          
  3: `${minioUrl}atmistanbul.png`,    
  4: `${minioUrl}reaktifbeton.jpg`,   
  5: `${minioUrl}0.jpg`,             
  6: `${minioUrl}0.jpg`,              
  7: `${minioUrl}0.jpg`,             
  8: `${minioUrl}atmpoint.png`,      
  9: `${minioUrl}aktifetkinlik.png`, 
  10: `${minioUrl}atmyonetimmerkezi.png`, 
  99: `${minioUrl}0.jpg`,           
};

const styles = `
  @media print {
    body * {
      visibility: hidden;
    }
    html, body {
      background-color: #fff !important;
    }
    .bg-light {
      background-color: #fff !important;
    }
    body {
      width: 21cm;
      height: 29.7cm;
    }
    .zimmet-page * {
      visibility: visible;
    }
    .zimmet-page {
      width: 100% !important;
      height: 100% !important;
      position: absolute;
      left: 0;
      top: 0;
      font-size: 20px !important;
    }
    .bg-dark {
      background-color: #ffffff !important;
    }
    .print-hide {
      display: none !important;
    }
    .ant-card {
      box-shadow: none !important;
      border: none !important;
    }
  }

  .zimmet-page {
    width: 21cm;
    min-height: 29.7cm;
    border: 1px solid #f1f1f1;
    box-shadow: 1px 2px 2px #c1c1c1;
    padding: 10px 30px;
    background-color: #fff;
    margin: 0 auto;
  }

  .zimmet-page hr {
    margin-top: 0.2rem !important;
    margin-bottom: 0.2rem !important;
  }

  .zimmet-page h6 {
    text-decoration: underline;
    font-size: 16px;
  }

  .zimmet-demirbas-table {
    border-collapse: collapse;
    font-size: 13px;
    text-align: center;
    border: 1px solid #000;
    width: 100%;
  }

  .zimmet-demirbas-table td,
  .zimmet-demirbas-table th {
    border-collapse: collapse;
    font-size: 13px;
    text-align: center;
    border: 1px solid #000;
    padding: 4px 6px;
  }

  .zimmet-demirbas-table thead td,
  .zimmet-demirbas-table thead th {
    font-size: 13px;
    font-weight: bold;
    background-color: #f5f5f5;
  }

  .zimmet-imza-table {
    border-collapse: collapse;
    font-size: 14px;
    text-align: center;
    width: 100%;
    margin-top: 30px;
  }

  .zimmet-imza-table td {
    border-collapse: collapse;
    width: 50%;
    padding: 8px;
  }

  .zimmet-imza-table thead td {
    border-bottom: 1px solid #000;
    font-weight: bold;
  }

  .zimmet-imza-table tbody td {
    height: 150px;
    vertical-align: top;
  }

  .zimmet-sartlar-table {
    width: 100%;
    font-size: 13px;
    margin-top: 20px;
  }

  .zimmet-sartlar-table td {
    padding: 4px 0;
  }

  .zimmet-baslik {
    text-align: center;
  }

  .zimmet-text {
    margin: 10px 0px;
  }

  .zimmet-tarih {
    text-align: right;
    margin-bottom: 20px;
  }

  .zimmet-container {
    padding: 20px;
  }

  .zimmet-actions {
    margin-bottom: 20px;
    display: flex;
    gap: 10px;
  }

  /* Dark mode adjustments for screen view */
  .dark .zimmet-page {
    background-color: #fff;
    color: #000;
  }
`;

const ZimmetTutanak = () => {
  const { kullaniciNo } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const { darkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const today = dayjs().format('DD.MM.YYYY');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/ik/zimmet-tutanak/${kullaniciNo}`, {
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        setData(result);
        
        if (result.kullanici?.ad_soyad) {
          document.title = `${result.kullanici.ad_soyad} - Zimmet Tutanağı`;
        }
      } catch (err) {
        console.error('Zimmet tutanak error:', err);
        setError('Zimmet tutanağı yüklenirken hata oluştu: ' + err.message);
        message.error('Zimmet tutanağı yüklenirken hata oluştu');
      } finally {
        setLoading(false);
      }
    };

    if (kullaniciNo) {
      fetchData();
    }
  }, [kullaniciNo, getAuthHeaders]);

  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    navigate(-1);
  };

  const getFirmaLogo = (firmaId) => {
    return firmaLogos[firmaId] || `${minioUrl}0.jpg`;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="zimmet-container">
        <Card>
          <Empty description={error} />
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Button onClick={handleBack} icon={<ArrowLeftOutlined />}>
              Geri Dön
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const { kullanici, zimmet_listesi } = data || {};
  const adSoyad = kullanici?.ad_soyad || '';
  const firmaId = kullanici?.firma_id;
  const firmaAdi = kullanici?.firma_adi || '';
  const logoUrl = getFirmaLogo(firmaId);

  return (
    <>
      <style>{styles}</style>
      <div className={`zimmet-container ${darkMode ? 'dark' : 'light'}`}>
        {/* Action Buttons */}
        <div className="zimmet-actions print-hide">
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleBack}
          >
            Geri
          </Button>
          <Button 
            type="primary" 
            icon={<PrinterOutlined />} 
            onClick={handlePrint}
          >
            Yazdır
          </Button>
        </div>

        {/* Printable Document */}
        <div className="zimmet-page" id="toPDF">
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-end',
            marginBottom: '20px'
          }}>
            <div style={{ flex: '0 0 55%' }}>
              <img 
                src={logoUrl} 
                alt={firmaAdi} 
                style={{ 
                  maxWidth: '100%',
                  maxHeight: '500px',
                  objectFit: 'contain'
                }} 
              />
            </div>
            <div style={{ textAlign: 'right' }}>
              <span style={{ fontWeight: '500' }}>Tarih: {today}</span>
            </div>
          </div>

          <div className="zimmet-text">
            {zimmet_listesi && zimmet_listesi.length > 0 ? (
              <>
                <p style={{ marginTop: '10px' }}>
                  Aşağıda bilgileri yer alan malzemeler belirtilen tarihlerde tarafıma teslim edilmiştir. 
                  İş bu tutanak kayda alınmıştır.
                </p>
                <p style={{ marginBottom: '10px' }}>Teslim alınan malzemeler:</p>

                {/* Demirbaş Table */}
                <table className="zimmet-demirbas-table">
                  <thead>
                    <tr>
                      <th colSpan={6}>Demirbaş</th>
                      <th colSpan={2}>Zimmet Etme</th>
                      <th colSpan={2}>İade Alma</th>
                    </tr>
                    <tr>
                      <th>No</th>
                      <th>Cinsi</th>
                      <th>Marka</th>
                      <th>Model</th>
                      <th>Seri No</th>
                      <th>Açıklama</th>
                      <th>Tarih</th>
                      <th>Durumu</th>
                      <th>Tarih</th>
                      <th>Durumu</th>
                    </tr>
                  </thead>
                  <tbody>
                    {zimmet_listesi.map((item, index) => {
                      // Build açıklama text
                      let aciklamaText = item.dl_aciklama || '';
                      if (item.zimmet_aciklama) {
                        aciklamaText += (aciklamaText ? ' ' : '') + `Teslim Açıklama: ${item.zimmet_aciklama}`;
                      }
                      if (item.teslim_aciklama) {
                        aciklamaText += (aciklamaText ? ' ' : '') + `İade Alma Açıklama: ${item.teslim_aciklama}`;
                      }

                      return (
                        <tr key={item.zimmet_liste_id || index}>
                          <td>{index + 1}</td>
                          <td>{item.demirbas_tip || '-'}</td>
                          <td>{item.marka || '-'}</td>
                          <td>{item.model || '-'}</td>
                          <td>{item.seri_no || '-'}</td>
                          <td style={{ textAlign: 'left', fontSize: '11px' }}>{aciklamaText || '-'}</td>
                          <td>{item.zimmet_tarihi || '-'}</td>
                          <td>{item.zimmet_durumu || '-'}</td>
                          <td>{item.iade_tarihi || '-'}</td>
                          <td>{item.iade_durumu || '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <br /><br />

                {/* Şartlar Table */}
                <table className="zimmet-sartlar-table">
                  <tbody>
                    <tr>
                      <td><h6>Not:</h6></td>
                    </tr>
                    <tr>
                      <td>
                        1-) Yukarıda özellikleri ve aksesuarları belirtilen malzemeleri eksiksiz ve çalışır durumda teslim aldım.
                      </td>
                    </tr>
                    <tr>
                      <td>
                        2-) Alet, Avadanlık ve eşyalar kimin üzerine zimmet edildi ise, bu malzemelerin sorumluluğu her ne şartlarda olursa olsun o kişiye aittir.
                      </td>
                    </tr>
                    <tr>
                      <td>
                        3-) Zimmetlenmiş malzemelerde, kullanımına engel olabilecek herhangi bir neden (çatlak, aşınma, deforme vb.) olduğu takdirde derhal zimmeti veren kişiye bildirilecektir.
                      </td>
                    </tr>
                    <tr>
                      <td>
                        4-) Şahsıma tevdi edilen malzemeyi usul ve yasalara uygun bir şekilde kullanmayı, muhafaza etmeyi ve işten ayrılma durumunda veya iadesi talep edildiğinde teslim aldığım gibi iade edeceğimi taahhüt ederim.
                      </td>
                    </tr>
                  </tbody>
                </table>

                {/* İmza Table */}
                <table className="zimmet-imza-table">
                  <thead>
                    <tr>
                      <td>Teslim Eden</td>
                      <td>Teslim Alan</td>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Zari Yalçın GÖKBUDAK</td>
                      <td>{adSoyad}</td>
                    </tr>
                  </tbody>
                </table>
              </>
            ) : (
              <p style={{ textAlign: 'center', marginTop: 50 }}>
                {adSoyad} - Zimmet Edilen Demirbaş Yok!
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ZimmetTutanak;